/**
 * Variant renderer for ResponsiveCommandSelect
 * Handles rendering of different UI variants (floating, inline, desktop, mobile)
 */

import React from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponsiveCommandSelectTrigger } from "../ResponsiveCommandSelectTrigger";
import { ResponsiveCommandSelectList } from "../ResponsiveCommandSelectList";
import { ResponsiveCommandSelectDesktop } from "../ResponsiveCommandSelectDesktop";
import { ResponsiveCommandSelectMobile } from "../ResponsiveCommandSelectMobile";
import type { SelectOption } from "../types";
import type { DropdownPosition, ResponsiveSelectListProps } from "@/lib/types/select-components";

interface ResponsiveSelectVariantProps {
  variant: "floating" | "inline-replace" | "inline-floating" | "desktop" | "mobile" | "center-screen";
  selected: SelectOption | null;
  placeholder: string;
  disabled: boolean;
  className?: string;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dropdownPosition: DropdownPosition | null;
  title: string;
  description?: string;
  listProps: ResponsiveSelectListProps;
  triggerWidth: number | null;
  highZIndex?: boolean;
  drawerId: string;
  tooltipContainer: HTMLElement | null;
  isDesktop: boolean;
  onOpenChangeProp?: (open: boolean) => void;
}

export function ResponsiveSelectVariant({
  variant,
  selected,
  placeholder,
  disabled,
  className,
  triggerRef,
  containerRef,
  dropdownRef,
  open,
  onOpenChange,
  dropdownPosition,
  title,
  description,
  listProps,
  triggerWidth,
  highZIndex,
  drawerId,
  tooltipContainer,
  isDesktop,
  onOpenChangeProp,
}: ResponsiveSelectVariantProps) {
  const trigger = (
    <ResponsiveCommandSelectTrigger
      selected={selected}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      triggerRef={triggerRef}
      onClick={
        isDesktop
          ? (e) => {
              e.stopPropagation();
              onOpenChange(!open);
            }
          : (e) => {
              e.preventDefault();
              e.stopPropagation();
              // Mobile drawer will be handled by parent
            }
      }
    />
  );

  // Floating variant: trigger stays, dropdown floats below
  if (variant === "inline-floating") {
    return (
      <div
        ref={containerRef}
        className="relative w-full"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ResponsiveCommandSelectTrigger
          selected={selected}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          triggerRef={triggerRef}
          tooltipContainer={tooltipContainer}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenChange(!open);
          }}
          aria-expanded={open}
          aria-haspopup="listbox"
        />
        {open && dropdownPosition && (
          <div
            ref={dropdownRef}
            role="listbox"
            aria-label={title}
            className={[
              "z-popover",
              "rounded-md border border-border bg-popover shadow-md",
              "max-h-[60vh] overflow-y-auto",
              // NOTE: do NOT add `zoom-in-*`/`animate-in` scale here. Under hub's
              // Tailwind v4 the enter animation never relaxes the scale back to 1,
              // leaving the panel stuck at scale(0.95) — it renders ~95% width and
              // shifted left, so it no longer fills the trigger. Keep it transform-free.
            ].join(" ")}
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxWidth: `min(${dropdownPosition.width}px, 95vw)`,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ResponsiveCommandSelectList {...listProps} />
          </div>
        )}
      </div>
    );
  }

  // Inline replace variant: trigger replaced by dropdown when open
  if (variant === "inline-replace") {
    return (
      <div className="relative w-full space-y-2">
        {open ? (
          <div
            className="relative z-popover max-h-[60vh] max-w-[95vw] overflow-y-auto rounded-md border bg-popover p-2 shadow-md space-y-2"
            data-popover-content
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            tabIndex={-1}
          >
            <div className="flex items-center justify-end mb-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenChange(false);
                }}
                aria-label="Close" title="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <ResponsiveCommandSelectList {...listProps} />
          </div>
        ) : (
          <ResponsiveCommandSelectTrigger
            selected={selected}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            triggerRef={triggerRef}
            tooltipContainer={tooltipContainer}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenChange(true);
            }}
          />
        )}
      </div>
    );
  }

  // Center on screen variant: use drawer
  if (variant === "center-screen") {
    return (
      <ResponsiveCommandSelectMobile
        drawerId={drawerId}
        title={title}
        description={description}
        trigger={
          <ResponsiveCommandSelectTrigger
            selected={selected}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            triggerRef={triggerRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Drawer opening handled by parent
            }}
          />
        }
        hasRecents={listProps.hasRecents}
        recentOptions={listProps.recentOptions}
        recentValueSet={listProps.recentValueSet}
        options={listProps.options}
        value={listProps.value}
        effectiveSearch={listProps.effectiveSearch}
        onSelect={listProps.onSelect}
        enableRecents={listProps.enableRecents}
        recentKey={listProps.recentKey}
        onClearRecents={listProps.onClearRecents}
        customOptionRenderer={listProps.customOptionRenderer}
      />
    );
  }

  // Desktop variant: use popover
  if (variant === "desktop") {
    return (
      <ResponsiveCommandSelectDesktop
        open={open}
        onOpenChange={onOpenChange}
        triggerWidth={triggerWidth}
        title={title}
        highZIndex={highZIndex}
        trigger={trigger}
        listProps={listProps}
        centerOnScreen={false}
      />
    );
  }

  // Mobile drawer variant
  return (
    <ResponsiveCommandSelectMobile
      drawerId={drawerId}
      title={title}
      description={description}
      trigger={trigger}
      hasRecents={listProps.hasRecents}
      recentOptions={listProps.recentOptions}
      recentValueSet={listProps.recentValueSet}
      options={listProps.options}
      value={listProps.value}
      effectiveSearch={listProps.effectiveSearch}
      onSelect={listProps.onSelect}
      enableRecents={listProps.enableRecents}
      recentKey={listProps.recentKey}
      onClearRecents={listProps.onClearRecents}
      customOptionRenderer={listProps.customOptionRenderer}
    />
  );
}
